<script>
  import { router } from '@inertiajs/svelte';
  import ApplicationLayout from '@/components/ApplicationLayout/index.svelte';
  import ConnectedAccount from '@/components/ConnectedAccount/index.svelte';
  
  let { notice = '', user = {} } = $props();
  
  function logOut() {
    if (user.current_session_id) {
      router.delete(`/sessions/${user.current_session_id}`, {
        onSuccess: () => {
          window.location.href = '/';
        }
      });
    } else {
      // If no session ID is available, redirect to sign in
      window.location.href = '/sign_in';
    }
  }
</script>

<ApplicationLayout {user}>
  <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
    {#if notice}
      <div class="mb-4 p-4 text-sm text-green-700 bg-green-100 rounded-lg" role="alert">
        {notice}
      </div>
    {/if}

    <div class="px-4 py-6 sm:px-0">
      <div class="mb-8">
        <h1 class="text-2xl font-semibold text-gray-900">Account Settings</h1>
        <p class="mt-1 text-sm text-gray-600">
          Manage your personal information and account security.
        </p>
      </div>

      <div class="space-y-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div class="md:grid md:grid-cols-3 md:gap-6">
          <div class="md:col-span-1">
            <h3 class="text-lg font-medium leading-6 text-gray-900">Profile</h3>
            <p class="mt-1 text-sm text-gray-500">
              Manage your account information.
            </p>
          </div>
          <div class="mt-5 md:mt-0 md:col-span-2 space-y-6">
            <div>
              <h3 class="text-sm font-medium text-gray-500">Email address</h3>
              <p class="mt-1 text-sm text-gray-900">{user.email}</p>
              <div class="mt-2">
                <a href="/identity/email/edit" class="text-sm font-medium text-brand-700 hover:text-brand-600">
                  Change email address
                </a>
              </div>
            </div>

            {#if user.oauth_user}
              <ConnectedAccount provider={user.provider} />
            {/if}

            <div>
              <h3 class="text-sm font-medium text-gray-500">Password</h3>
              <p class="mt-1 text-sm text-gray-900">••••••••</p>
              <div class="mt-2">
                <a href="/password/edit" class="text-sm font-medium text-brand-700 hover:text-brand-600">
                  Change password
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="md:grid md:grid-cols-3 md:gap-6 border-t border-gray-200 pt-6">
          <div class="md:col-span-1">
            <h3 class="text-lg font-medium leading-6 text-gray-900">Security</h3>
            <p class="mt-1 text-sm text-gray-500">
              Manage your security settings and options.
            </p>
          </div>
          <div class="mt-5 md:mt-0 md:col-span-2 space-y-6">
            <div>
              <h3 class="text-sm font-medium text-gray-500">Two-factor authentication</h3>
              <p class="mt-1 text-sm text-gray-900">
                {#if user.otp_required_for_sign_in}
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Enabled
                  </span>
                {:else}
                  <span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                    Not enabled
                  </span>
                {/if}
              </p>
              <div class="mt-3 space-x-4">
                <a href="/two_factor_authentication/profile/totp/new" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-600">
                  {user.otp_required_for_sign_in ? 'Manage 2FA' : 'Enable 2FA'}
                </a>
                
                {#if user.otp_required_for_sign_in}
                  <a href="/two_factor_authentication/profile/recovery_codes" class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-600">
                    Recovery codes
                  </a>
                  
                  <a href="/two_factor_authentication/profile/security_keys" class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-600">
                    Security keys
                  </a>
                {/if}
              </div>
            </div>

            <div>
              <h3 class="text-sm font-medium text-gray-500">Sessions</h3>
              <p class="mt-1 text-sm text-gray-500">Manage your active sessions.</p>
              <div class="mt-2">
                <a href="/sessions" class="text-sm font-medium text-brand-700 hover:text-brand-600">
                  View all logged in sessions
                </a>
              </div>
            </div>
          </div>
        </div>

        <div class="md:grid md:grid-cols-3 md:gap-6 border-t border-gray-200 pt-6">
          <div class="md:col-span-1">
            <h3 class="text-lg font-medium leading-6 text-gray-900">Invitations</h3>
            <p class="mt-1 text-sm text-gray-500">
              Invite other people to your account.
            </p>
          </div>
          <div class="mt-5 md:mt-0 md:col-span-2">
            <div class="mt-2">
              <button
                disabled
                class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-400 cursor-not-allowed"
              >
                Send invitation
                <span class="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">Coming Soon</span>
              </button>
            </div>
          </div>
        </div>

        <div class="md:grid md:grid-cols-3 md:gap-6 border-t border-gray-200 pt-6">
          <div class="md:col-span-1">
            <h3 class="text-lg font-medium leading-6 text-gray-900">Account</h3>
            <p class="mt-1 text-sm text-gray-500">
              Manage your account.
            </p>
          </div>
          <div class="mt-5 md:mt-0 md:col-span-2">
            <div class="mt-2">
              <button
                onclick={logOut}
                class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</ApplicationLayout>